import React from 'react';
import { Components } from 'react-markdown';
import { HighlightedText } from '../HighlightedText';
import { CodeBlock } from './CodeBlock';
import { renderKatexCached } from '../../utils/katexRenderer';

// ── Shared search-highlight helpers ──

type ProcessChildren = (node: React.ReactNode, index?: number) => React.ReactNode;

interface SearchCtx {
	searchQuery?: string;
	currentMatchIndexInMessage: number;
	matchCounterRef: React.MutableRefObject<number>;
}

/**
 * For elements whose children are always recursively processed (strong, em, del,
 * thead, tbody, tr, th, td, li, etc.) — just map children through processChildren.
 */
function withProcessChildren(
	Tag: string,
	className: string,
	processChildren: ProcessChildren,
	extraStyle?: React.CSSProperties,
) {
	return function Renderer({ children, ...props }: any) {
		const styleProps = extraStyle ? { style: extraStyle, ...props } : props;
		return React.createElement(
			Tag,
			{ className, ...styleProps },
			React.Children.map(children, (child, i) => processChildren(child, i)),
		);
	};
}

/**
 * For leaf-text elements (h1-h6, blockquote, ul, ol, a, inline code, table) where
 * we check `typeof children === 'string'` and conditionally wrap in HighlightedText.
 */
function withHighlight(
	Tag: string,
	className: string,
	ctx: SearchCtx,
	extraProps?: Record<string, any>,
	extraStyle?: React.CSSProperties,
) {
	return function Renderer({ children, ...props }: any) {
		const merged = { ...props, ...extraProps };
		const styleProps = extraStyle ? { style: extraStyle, ...merged } : merged;
		if (ctx.searchQuery && typeof children === 'string') {
			return React.createElement(
				Tag,
				{ className, ...styleProps },
				<HighlightedText
					text={children}
					searchQuery={ctx.searchQuery}
					currentMatchIndex={ctx.currentMatchIndexInMessage}
					matchCounterRef={ctx.matchCounterRef}
				/>,
			);
		}
		return React.createElement(Tag, { className, ...styleProps }, children);
	};
}

export function createMarkdownComponents(
	searchQuery: string | undefined,
	currentMatchIndexInMessage: number,
	matchCounterRef: React.MutableRefObject<number>,
	processChildren: ProcessChildren,
): Components {
	const ctx: SearchCtx = { searchQuery, currentMatchIndexInMessage, matchCounterRef };

	return {
		div({ className, children, ...props }) {
			if (className?.includes('katex-display')) {
				return (
					<div
						className={`overflow-x-auto overflow-y-hidden my-sm ${className || ''}`}
						{...props}
					>
						{children}
					</div>
				);
			}
			return (
				<div className={className} {...props}>
					{React.Children.map(children, (child, i) => processChildren(child, i))}
				</div>
			);
		},

		span({ className, children, ...props }) {
			if (className?.includes('katex')) {
				return (
					<span className={className} {...props}>
						{children}
					</span>
				);
			}
			return (
				<span className={className} {...props}>
					{React.Children.map(children, (child, i) => processChildren(child, i))}
				</span>
			);
		},

		p({ children, ...props }) {
			const isInListItem = (props.node as any)?.parent?.tagName === 'li';
			const className = isInListItem ? 'mb-0 leading-relaxed' : 'mb-md leading-relaxed';
			return (
				<p className={className} {...props}>
					{React.Children.map(children, (child, i) => processChildren(child, i))}
				</p>
			);
		},

		// ── Code blocks & math ──
		pre({ children, node, className, ...props }) {
			const isMathDisplay = className?.includes('math-display');

			if (isMathDisplay) {
				let mathText =
					typeof children === 'string'
						? children
						: String(React.Children.toArray(children).join(''));
				mathText = mathText
					.replace(/&lt;/g, '<')
					.replace(/&gt;/g, '>')
					.replace(/&amp;/g, '&');
				const rendered = renderKatexCached(mathText, true);
				if (rendered.ok) {
					// eslint-disable-next-line react/no-danger -- KaTeX renderToString produces sanitized math-only HTML
					// nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
					return <div className="overflow-x-auto overflow-y-hidden my-sm" dangerouslySetInnerHTML={{ __html: rendered.html }} />;
				}
				return (
					<pre className="font-code bg-neutral-600 p-sm rounded-medium overflow-hidden max-w-full whitespace-pre-wrap break-words" {...props}>
						{children}
					</pre>
				);
			}

			const codeHast = (node?.children as any[])?.find(
				(c: any) => c.type === 'element' && c.tagName === 'code',
			);

			if (codeHast) {
				const cls = codeHast.properties?.className;
				const isMathDisp = Array.isArray(cls) ? cls.includes('math-display') : false;
				if (isMathDisp) {
					let mathText = codeHast.children?.[0]?.value || '';
					mathText = mathText
						.replace(/&lt;/g, '<')
						.replace(/&gt;/g, '>')
						.replace(/&amp;/g, '&');
					const rendered = renderKatexCached(mathText, true);
					if (rendered.ok) {
						// eslint-disable-next-line react/no-danger -- KaTeX renderToString produces sanitized math-only HTML
						// nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
						return <div className="overflow-x-auto overflow-y-hidden my-sm" dangerouslySetInnerHTML={{ __html: rendered.html }} />;
					}
					return (
						<pre className="font-code bg-neutral-600 p-sm rounded-medium overflow-hidden max-w-full whitespace-pre-wrap break-words" {...props}>
							{children}
						</pre>
					);
				}
			}

			const child = React.Children.toArray(children)[0] as React.ReactElement<{
				className?: string;
				children?: React.ReactNode;
			}>;

			let codeContent = '';
			if (codeHast?.children?.[0]?.type === 'text') {
				codeContent = codeHast.children[0].value;
			} else if (React.isValidElement(child) && child.props?.children) {
				const childContent = child.props.children;
				if (typeof childContent === 'string') {
					codeContent = childContent;
				} else if (Array.isArray(childContent)) {
					codeContent = childContent.map((c) => (typeof c === 'string' ? c : '')).join('');
				}
			}

			if (React.isValidElement(child) && child.props?.className) {
				const match = /language-(\w+)/.exec(child.props.className || '');
				return (
					<CodeBlock
						language={match ? match[1] : undefined}
						value={codeContent.replace(/\n$/, '')}
						searchQuery={searchQuery}
						currentMatchIndex={currentMatchIndexInMessage}
						matchCounterRef={matchCounterRef}
					/>
				);
			}
			if (React.isValidElement(child) && child.props) {
				return (
					<CodeBlock
						value={codeContent.replace(/\n$/, '')}
						searchQuery={searchQuery}
						currentMatchIndex={currentMatchIndexInMessage}
						matchCounterRef={matchCounterRef}
					/>
				);
			}
			return (
				<pre className="font-code bg-neutral-600 p-sm rounded-medium overflow-hidden max-w-full whitespace-pre-wrap break-words" {...props}>
					{children}
				</pre>
			);
		},

		code({ className, children, ...props }) {
			const isMathInline = className?.includes('math-inline');
			if (isMathInline) {
				let mathText =
					typeof children === 'string'
						? children
						: String(React.Children.toArray(children).join(''));
				mathText = mathText
					.replace(/&lt;/g, '<')
					.replace(/&gt;/g, '>')
					.replace(/&amp;/g, '&');
				const rendered = renderKatexCached(mathText, false);
				if (rendered.ok) {
					// eslint-disable-next-line react/no-danger -- KaTeX renderToString produces sanitized math-only HTML
					// nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
					return <span dangerouslySetInnerHTML={{ __html: rendered.html }} />;
				}
				return <code className={className} {...props}>{children}</code>;
			}
			if (className?.includes('math-display') || className?.includes('language-')) {
				return <code className={className} {...props}>{children}</code>;
			}
			if (searchQuery && typeof children === 'string') {
				return (
					<code className="bg-neutral-400 text-blue-300 px-1 py-0.5 rounded-sm font-code text-sm" {...props}>
						<HighlightedText
							text={children}
							searchQuery={searchQuery}
							currentMatchIndex={currentMatchIndexInMessage}
							matchCounterRef={matchCounterRef}
						/>
					</code>
				);
			}
			return (
				<code className="bg-neutral-400 text-blue-300 px-1 py-0.5 rounded-sm font-code text-sm" {...props}>
					{children}
				</code>
			);
		},

		// ── Links ──
		a({ children, ...props }) {
			const linkClass = 'text-blue-400 hover:text-blue-300 underline transition-colors duration-200';
			if (searchQuery && typeof children === 'string') {
				return (
					<a className={linkClass} {...props} target="_blank" rel="noopener noreferrer">
						<HighlightedText
							text={children}
							searchQuery={searchQuery}
							currentMatchIndex={currentMatchIndexInMessage}
							matchCounterRef={matchCounterRef}
						/>
					</a>
				);
			}
			return (
				<a className={linkClass} {...props} target="_blank" rel="noopener noreferrer">
					{children}
				</a>
			);
		},

		// ── Headings (DRYed with withHighlight) ──
		h1: withHighlight('h1', 'text-xl font-semibold text-neutral-100 mt-xl mb-md border-b border-neutral-400 pb-xs', ctx) as any,
		h2: withHighlight('h2', 'text-lg font-semibold text-neutral-100 mt-lg mb-sm', ctx) as any,
		h3: withHighlight('h3', 'text-md font-medium text-neutral-100 mt-md mb-sm', ctx) as any,
		h4: withHighlight('h4', 'text-md font-medium text-neutral-200 mt-sm mb-xs', ctx) as any,
		h5: withHighlight('h5', 'text-sm font-medium text-neutral-200 mt-sm mb-xs', ctx) as any,
		h6: withHighlight('h6', 'text-sm font-regular text-neutral-300 mt-sm mb-xs', ctx) as any,

		// ── Lists ──
		ul: withHighlight('ul', 'list-disc list-inside mb-0 pl-md', ctx, undefined, { marginTop: '0.25rem' }) as any,
		ol: withHighlight('ol', 'list-decimal list-inside mb-0 pl-md', ctx, undefined, { marginTop: '0.25rem' }) as any,
		li: withProcessChildren('li', 'leading-relaxed', processChildren, { marginBottom: '0.25rem', display: 'list-item' }) as any,

		// ── Blockquote ──
		blockquote: withHighlight('blockquote', 'border-l-4 border-neutral-400 pl-md py-xs my-md bg-neutral-600 rounded-r-medium italic text-neutral-200', ctx) as any,

		// ── Tables ──
		table({ children, ...props }) {
			if (searchQuery && typeof children === 'string') {
				return (
					<div className="overflow-hidden my-md max-w-full">
						<table className="w-full border-collapse border border-neutral-400 rounded-medium overflow-hidden" {...props}>
							<HighlightedText
								text={children}
								searchQuery={searchQuery}
								currentMatchIndex={currentMatchIndexInMessage}
								matchCounterRef={matchCounterRef}
							/>
						</table>
					</div>
				);
			}
			return (
				<div className="overflow-hidden my-md max-w-full">
					<table className="w-full border-collapse border border-neutral-400 rounded-medium overflow-hidden" {...props}>
						{children}
					</table>
				</div>
			);
		},
		thead: withProcessChildren('thead', 'bg-neutral-500', processChildren) as any,
		tbody: withProcessChildren('tbody', '', processChildren) as any,
		tr: withProcessChildren('tr', 'border-b border-neutral-400 hover:bg-neutral-600 transition-colors duration-150', processChildren) as any,
		th: withProcessChildren('th', 'text-left p-sm font-medium text-neutral-100 border-r border-neutral-400 last:border-r-0', processChildren) as any,
		td: withProcessChildren('td', 'p-sm text-neutral-200 border-r border-neutral-400 last:border-r-0', processChildren) as any,

		// ── Misc ──
		hr({ ...props }) {
			return <hr className="border-0 h-px bg-neutral-400 my-lg" {...props} />;
		},
		strong: withProcessChildren('strong', 'font-semibold text-neutral-100', processChildren) as any,
		em: withProcessChildren('em', 'italic text-neutral-200', processChildren) as any,
		del: withProcessChildren('del', 'line-through text-neutral-300', processChildren) as any,
	};
}
